import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Terminal, Download, Trash2, Pause, Play, RefreshCw, AlertCircle, CheckCircle, HardDrive, Settings, X, Shield, ShieldOff, ShieldAlert } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || '';

// ─── Disk Alert Banner ────────────────────────────────────────────────────────
const DiskAlertBanner = ({ disk, onConfigure }) => {
  const [dismissed, setDismissed] = useState(false);
  if (!disk || !disk.alert || dismissed) return null;

  const isCritical = disk.level === 'CRITICAL';
  return (
    <div className={`rounded-lg p-3 border flex items-center gap-3 ${
      isCritical
        ? 'bg-red-500/15 border-red-500/50'
        : 'bg-yellow-500/15 border-yellow-500/50'
    }`}>
      <HardDrive className={`w-5 h-5 flex-shrink-0 ${isCritical ? 'text-red-400' : 'text-yellow-400'}`} />
      <div className="flex-1">
        <div className={`font-semibold text-sm ${isCritical ? 'text-red-400' : 'text-yellow-400'}`}>
          {isCritical ? '🔴 Disque CRITIQUE' : '🟡 Disque sous surveillance'}
        </div>
        <div className="text-xs text-gray-400 mt-0.5">
          {disk.message} — Seuils : avertissement {disk.warn_threshold}% / critique {disk.critical_threshold}%
        </div>
        {/* Barre visuelle */}
        <div className="mt-1.5 w-full bg-gray-700 rounded-full h-1.5">
          <div
            className={`h-1.5 rounded-full transition-all duration-500 ${
              isCritical ? 'bg-red-500' : 'bg-yellow-500'
            }`}
            style={{ width: `${Math.min(disk.percent, 100)}%` }}
          />
        </div>
        <div className="text-xs text-gray-500 mt-0.5">
          {disk.used_gb} GB / {disk.total_gb} GB used ({disk.percent}%) — {disk.free_gb} GB free
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <button
          onClick={onConfigure}
          className="px-2 py-1 text-xs bg-gray-700 text-gray-300 rounded hover:bg-gray-600 flex items-center gap-1"
        >
          <Settings className="w-3 h-3" />
          Seuils
        </button>
        <button onClick={() => setDismissed(true)} className="text-gray-500 hover:text-gray-300">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

// ─── Disk Threshold Config Modal ──────────────────────────────────────────────
const DiskThresholdModal = ({ current, onSave, onClose }) => {
  const [warn,     setWarn]     = useState(current?.warn     ?? 80);
  const [critical, setCritical] = useState(current?.critical ?? 90);
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState('');

  const handleSave = async () => {
    if (warn >= critical) { setError('Warning threshold must be lower than critical threshold.'); return; }
    if (warn <= 0 || critical > 100) { setError('Valeurs invalides (1-100).'); return; }
    setSaving(true);
    try {
      const token = localStorage.getItem('access_token');
      const res = await fetch(`${API_BASE}/api/v1/system/disk/thresholds`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ warn, critical }),
      });
      const data = await res.json();
      if (data.success) { onSave({ warn, critical }); onClose(); }
      else setError(data.error || 'Erreur de sauvegarde');
    } catch (e) {
      setError('Network error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
      <div className="bg-gray-900 border border-gray-700 rounded-xl p-6 w-full max-w-sm shadow-2xl">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-bold text-white flex items-center gap-2">
            <HardDrive className="w-5 h-5 text-brand-green" />
            Seuils d'alerte disque
          </h3>
          <button onClick={onClose} className="text-gray-500 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-sm text-gray-400 block mb-1">
              Avertissement (%) — actuellement <span className="text-yellow-400">{warn}%</span>
            </label>
            <input
              type="range" min="50" max="95" value={warn}
              onChange={e => setWarn(Number(e.target.value))}
              className="w-full accent-yellow-400"
            />
            <div className="flex justify-between text-xs text-gray-600 mt-1">
              <span>50%</span><span>95%</span>
            </div>
          </div>

          <div>
            <label className="text-sm text-gray-400 block mb-1">
              Critique (%) — actuellement <span className="text-red-400">{critical}%</span>
            </label>
            <input
              type="range" min="60" max="99" value={critical}
              onChange={e => setCritical(Number(e.target.value))}
              className="w-full accent-red-400"
            />
            <div className="flex justify-between text-xs text-gray-600 mt-1">
              <span>60%</span><span>99%</span>
            </div>
          </div>

          {/* Preview */}
          <div className="bg-gray-800 rounded-lg p-3 text-xs text-gray-400 space-y-1">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-brand-green inline-block" /> &lt; {warn}% → OK
            </div>
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-yellow-500 inline-block" /> {warn}%–{critical}% → Avertissement
            </div>
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-red-500 inline-block" /> ≥ {critical}% → Critique
            </div>
          </div>

          {error && <div className="text-red-400 text-xs">{error}</div>}

          <div className="flex gap-3 pt-1">
            <button onClick={onClose}
              className="flex-1 px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 text-sm">
              Annuler
            </button>
            <button onClick={handleSave} disabled={saving}
              className="flex-1 px-4 py-2 bg-brand-green text-brand-black rounded-lg hover:bg-green-600 text-sm font-semibold disabled:opacity-50">
              {saving ? 'Sauvegarde...' : 'Enregistrer'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Fail2ban View ────────────────────────────────────────────────────────────
const Fail2banView = ({ data, loading, onRefresh }) => {
  const [tab, setTab] = useState('banned'); // 'banned' | 'events'

  if (loading) return (
    <div className="text-gray-600 text-center py-12">
      <RefreshCw className="w-10 h-10 mx-auto mb-3 opacity-50 animate-spin" />
      <p>Chargement fail2ban...</p>
    </div>
  );

  if (!data || !data.available) return (
    <div className="text-center py-12 text-gray-500">
      <ShieldOff className="w-10 h-10 mx-auto mb-3 opacity-40" />
      <p className="font-medium text-sm">fail2ban non accessible</p>
      <p className="text-xs mt-1">{data?.message || 'Montage volume /var/log/fail2ban.log requis'}</p>
    </div>
  );

  const { banned = [], recent_events = [], stats: f2bStats = {} } = data;

  return (
    <div className="space-y-3">
      {/* Stats bar */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-gray-900 rounded-lg p-3 border border-gray-700 text-center">
          <div className="text-xl font-bold text-red-400">{f2bStats.total_banned ?? 0}</div>
          <div className="text-xs text-gray-400">IPs bannies</div>
        </div>
        <div className="bg-gray-900 rounded-lg p-3 border border-gray-700 text-center">
          <div className="text-xl font-bold text-orange-400">{f2bStats.total_bans_today ?? 0}</div>
          <div className="text-xs text-gray-400">Bans aujourd'hui</div>
        </div>
        <div className="bg-gray-900 rounded-lg p-3 border border-gray-700 text-center">
          <div className="text-xl font-bold text-blue-400">{(f2bStats.jails ?? []).length}</div>
          <div className="text-xs text-gray-400">Jails actifs</div>
        </div>
      </div>

      {/* Jails list */}
      {(f2bStats.jails ?? []).length > 0 && (
        <div className="flex gap-2 flex-wrap">
          {f2bStats.jails.map(j => (
            <span key={j} className="px-2 py-0.5 bg-blue-500/20 text-blue-300 text-xs rounded-full border border-blue-500/30">
              {j}
            </span>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-700 pb-2">
        <button
          onClick={() => setTab('banned')}
          className={`px-3 py-1.5 rounded text-sm transition-colors ${tab === 'banned'
            ? 'bg-red-500/20 text-red-300 font-semibold'
            : 'text-gray-400 hover:text-gray-300'}`}
        >
          <ShieldAlert className="w-3.5 h-3.5 inline mr-1" />
          IPs bannies ({banned.length})
        </button>
        <button
          onClick={() => setTab('events')}
          className={`px-3 py-1.5 rounded text-sm transition-colors ${tab === 'events'
            ? 'bg-brand-green/20 text-brand-green font-semibold'
            : 'text-gray-400 hover:text-gray-300'}`}
        >
          <Shield className="w-3.5 h-3.5 inline mr-1" />
          Recent events ({recent_events.length})
        </button>
      </div>

      {/* Banned IPs table */}
      {tab === 'banned' && (
        <div className="bg-black rounded-lg border border-red-500/20 overflow-hidden">
          <div className="bg-brand-black-lighter px-3 py-2 border-b border-gray-700 flex items-center gap-2">
            <ShieldAlert className="w-3.5 h-3.5 text-red-400" />
            <span className="text-xs text-red-400 font-mono">IPs actuellement bannies</span>
          </div>
          <div className="overflow-y-auto max-h-80 font-mono text-xs">
            {banned.length === 0 ? (
              <div className="text-center text-gray-600 py-8">
                <Shield className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <p>Aucune IP bannie pour l'instant</p>
              </div>
            ) : (
              <table className="w-full">
                <thead className="sticky top-0 bg-gray-900 text-gray-500 text-xs">
                  <tr>
                    <th className="px-3 py-1.5 text-left">IP</th>
                    <th className="px-3 py-1.5 text-left">Jail</th>
                    <th className="px-3 py-1.5 text-left">Banni le</th>
                  </tr>
                </thead>
                <tbody>
                  {banned.map((b, i) => (
                    <tr key={i} className="hover:bg-red-900/10 border-t border-gray-800">
                      <td className="px-3 py-1 text-red-300 font-medium">{b.ip}</td>
                      <td className="px-3 py-1 text-blue-400">{b.jail}</td>
                      <td className="px-3 py-1 text-gray-500">{b.banned_at?.replace('T', ' ').slice(0, 19) || '--'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Recent events */}
      {tab === 'events' && (
        <div className="bg-black rounded-lg border border-brand-green/20 overflow-hidden">
          <div className="bg-brand-black-lighter px-3 py-2 border-b border-gray-700 flex items-center gap-2">
            <Shield className="w-3.5 h-3.5 text-brand-green" />
            <span className="text-xs text-brand-green font-mono">Ban / Unban Events</span>
          </div>
          <div className="overflow-y-auto max-h-80 p-2 space-y-0.5 font-mono text-xs">
            {recent_events.length === 0 ? (
              <div className="text-center text-gray-600 py-8">No events</div>
            ) : recent_events.map((ev, i) => (
              <div key={i} className={`px-2 py-0.5 rounded flex items-center gap-2 ${
                ev.action === 'Ban' ? 'hover:bg-red-900/15' : 'hover:bg-green-900/15'
              }`}>
                <span className="text-gray-600 shrink-0">{ev.timestamp?.slice(0, 19).replace('T', ' ')}</span>
                <span className={`font-bold shrink-0 w-12 text-center rounded px-1 text-xs ${
                  ev.action === 'Ban'
                    ? 'text-red-300 bg-red-500/20'
                    : 'text-green-300 bg-green-500/20'
                }`}>{ev.action}</span>
                <span className="text-blue-400 shrink-0">[{ev.jail}]</span>
                <span className={ev.action === 'Ban' ? 'text-red-200' : 'text-gray-400'}>{ev.ip}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};


// ─── Main LogsViewer ──────────────────────────────────────────────────────────
const LogsViewer = ({ fullHeight = false }) => {
  const [logType, setLogType]     = useState('backend');
  const [logs, setLogs]           = useState([]);
  const [paused, setPaused]       = useState(false);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState(null);
  const [stats, setStats]         = useState({ total: 0, errors: 0, warnings: 0 });
  const [disk, setDisk]           = useState(null);
  const [thresholds, setThresholds] = useState({ warn: 80, critical: 90 });
  const [showConfig, setShowConfig] = useState(false);
  const [filterLevel, setFilterLevel] = useState('ALL');
  const [fail2banData, setFail2banData] = useState(null);
  const [fail2banLoading, setFail2banLoading] = useState(false);
  const logsEndRef = useRef(null);
  const initialLoadDone = useRef(false);

  const getAuthHeaders = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('access_token')}`,
  });

  // Charger les seuils + statut disque
  const fetchDiskStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/v1/system/disk/status`, { headers: getAuthHeaders() });
      if (res.ok) setDisk(await res.json());
    } catch {}
  }, []);

  const fetchThresholds = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/v1/system/disk/thresholds`, { headers: getAuthHeaders() });
      if (res.ok) setThresholds(await res.json());
    } catch {}
  }, []);

  // Load fail2ban data
  const fetchFail2ban = useCallback(async () => {
    setFail2banLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/v1/system/fail2ban/banned`, { headers: getAuthHeaders() });
      if (res.ok) setFail2banData(await res.json());
    } catch (e) {
      setFail2banData({ available: false, message: `Erreur: ${e.message}`, banned: [], recent_events: [], stats: {} });
    } finally {
      setFail2banLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDiskStatus();
    fetchThresholds();
    const interval = setInterval(fetchDiskStatus, 30000); // check disque toutes les 30s
    return () => clearInterval(interval);
  }, [fetchDiskStatus, fetchThresholds]);

  // Load fail2ban when the tab is selected
  useEffect(() => {
    if (logType === 'fail2ban') {
      fetchFail2ban();
      const interval = setInterval(fetchFail2ban, 30000);
      return () => clearInterval(interval);
    }
  }, [logType, fetchFail2ban]);

  // Charger les logs
  const fetchLogs = useCallback(async (isInitial = false) => {
    if (paused && !isInitial) return;
    try {
      const endpoint = logType === 'backend'
        ? '/api/v1/system/logs/backend'
        : logType === 'frontend'
        ? '/api/v1/system/logs/frontend'
        : '/api/v1/system/logs/all';

      const res = await fetch(`${API_BASE}${endpoint}?lines=300`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();

      if (data.error && (!data.logs || data.logs.length === 0)) {
        setError(data.error);
      } else {
        setError(null);
        const transformed = (data.logs || []).map((log, idx) => ({
          id:          log.id || Date.now() + idx,
          source:      log.source    || 'system',
          level:       log.level     || 'INFO',
          message:     log.message   || log.raw || '',
          timestamp:   log.timestamp ? new Date(log.timestamp) : new Date(),
          module:      log.module,
          method:      log.method,
          path:        log.path,
          status_code: log.status_code,
        }));
        setLogs(transformed);
        setStats({
          total:    data.total    || transformed.length,
          errors:   data.errors   || transformed.filter(l => l.level === 'ERROR').length,
          warnings: data.warnings || transformed.filter(l => l.level === 'WARNING').length,
        });
      }
    } catch (err) {
      setError(`Erreur de connexion: ${err.message}`);
    }
  }, [logType, paused]);

  useEffect(() => {
    if (!initialLoadDone.current) {
      setLoading(true);
      fetchLogs(true).finally(() => { setLoading(false); initialLoadDone.current = true; });
    }
  }, [fetchLogs]);

  useEffect(() => {
    if (paused) return;
    const interval = setInterval(() => fetchLogs(false), 5000);
    return () => clearInterval(interval);
  }, [fetchLogs, paused]);

  useEffect(() => {
    if (!paused && logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs, paused]);

  useEffect(() => {
    initialLoadDone.current = false;
    setLoading(true);
    fetchLogs(true).finally(() => { setLoading(false); initialLoadDone.current = true; });
  }, [logType]);

  const getLogColor = (level) => {
    switch (level) {
      case 'ERROR':    return 'text-red-400';
      case 'WARNING':  return 'text-yellow-400';
      case 'SUCCESS':  return 'text-green-400';
      case 'CRITICAL': return 'text-red-300 font-bold';
      case 'DEBUG':    return 'text-purple-400';
      default:         return 'text-gray-300';
    }
  };

  const getLogBg = (level) => {
    switch (level) {
      case 'ERROR':    return 'hover:bg-red-900/20 border-l-2 border-red-600/40';
      case 'WARNING':  return 'hover:bg-yellow-900/20 border-l-2 border-yellow-600/40';
      case 'CRITICAL': return 'hover:bg-red-900/30 border-l-2 border-red-400';
      case 'SUCCESS':  return 'hover:bg-green-900/20 border-l-2 border-green-600/40';
      default:         return 'hover:bg-gray-900/30';
    }
  };

  const formatTimestamp = (date) => {
    if (!date || !(date instanceof Date)) return '--:--:--';
    return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const formatMessage = (log) => {
    if (log.method && log.path && log.status_code) {
      const color = log.status_code >= 500 ? 'text-red-400'
                  : log.status_code >= 400 ? 'text-yellow-400'
                  : 'text-green-400';
      return (
        <>
          <span className="text-cyan-400">{log.method}</span>{' '}
          <span className="text-gray-200">{log.path}</span>{' '}
          <span className={color}>{log.status_code}</span>
        </>
      );
    }
    return log.message;
  };

  const filteredLogs = filterLevel === 'ALL'
    ? logs
    : logs.filter(l => l.level === filterLevel);

  const downloadLogs = () => {
    const content = filteredLogs
      .map(l => `[${l.timestamp?.toISOString?.() || ''}] [${l.source}] [${l.level}] ${l.message}`)
      .join('\n');
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sylink-logs-${logType}-${Date.now()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Indicateur disque dans le header (compact)
  const DiskIndicator = () => {
    if (!disk) return null;
    const color = disk.level === 'CRITICAL' ? 'text-red-400'
                : disk.level === 'WARNING'  ? 'text-yellow-400'
                : 'text-green-400';
    const bg    = disk.level === 'CRITICAL' ? 'bg-red-500/20 border-red-500/40'
                : disk.level === 'WARNING'  ? 'bg-yellow-500/20 border-yellow-500/40'
                : 'bg-green-500/10 border-green-500/20';
    return (
      <button
        onClick={() => setShowConfig(true)}
        className={`flex items-center gap-1.5 px-2 py-1 rounded border text-xs ${bg} ${color} hover:opacity-80 transition-opacity`}
        title="Cliquez pour configurer les seuils"
      >
        <HardDrive className="w-3.5 h-3.5" />
        <span>Disque {disk.percent}%</span>
        {disk.alert && <span className="animate-pulse">●</span>}
      </button>
    );
  };

  return (
    <div className={`${fullHeight ? 'h-full flex flex-col' : ''} space-y-4`}>

      {/* Disk alert banner */}
      <DiskAlertBanner disk={disk} onConfigure={() => setShowConfig(true)} />

      {/* Modal config seuils */}
      {showConfig && (
        <DiskThresholdModal
          current={thresholds}
          onSave={t => { setThresholds(t); fetchDiskStatus(); }}
          onClose={() => setShowConfig(false)}
        />
      )}

      {/* Controls */}
      <div className="bg-brand-black rounded-lg p-4 border border-gray-800">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-white flex items-center gap-2">
            <Terminal className="w-5 h-5 text-brand-green" />
            System Logs
          </h3>
          <div className="flex items-center gap-2">
            <DiskIndicator />
            <button onClick={() => { setLoading(true); fetchLogs(true).finally(() => setLoading(false)); }}
              disabled={loading}
              className="px-3 py-1.5 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors flex items-center gap-1.5 text-sm disabled:opacity-50">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
              Actualiser
            </button>
            <button onClick={() => setPaused(!paused)}
              className={`px-3 py-1.5 rounded-lg transition-colors flex items-center gap-1.5 text-sm ${
                paused ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'
              }`}>
              {paused ? <Play className="w-3.5 h-3.5" /> : <Pause className="w-3.5 h-3.5" />}
              {paused ? 'Reprendre' : 'Pause'}
            </button>
            <button onClick={() => { setLogs([]); setStats({ total: 0, errors: 0, warnings: 0 }); }}
              className="px-3 py-1.5 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors flex items-center gap-1.5 text-sm">
              <Trash2 className="w-3.5 h-3.5" />
              Effacer
            </button>
            <button onClick={downloadLogs} disabled={filteredLogs.length === 0}
              className="px-3 py-1.5 bg-brand-green/20 text-brand-green rounded-lg hover:bg-brand-green/30 transition-colors flex items-center gap-1.5 text-sm disabled:opacity-50">
              <Download className="w-3.5 h-3.5" />
              Exporter
            </button>
          </div>
        </div>

        {/* Source + Filtre niveau */}
        <div className="flex gap-2 flex-wrap">
          {[
            { id: 'backend',  label: 'Backend (API)' },
            { id: 'frontend', label: 'Frontend (Nginx)' },
            { id: 'both',     label: 'Les Deux' },
            { id: 'fail2ban', label: '🛡️ Fail2ban', red: true },
          ].map(t => (
            <button key={t.id} onClick={() => setLogType(t.id)}
              className={`px-4 py-1.5 rounded-lg transition-colors text-sm ${
                logType === t.id
                  ? t.red
                    ? 'bg-red-500/30 text-red-300 font-semibold border border-red-500/50'
                    : 'bg-brand-green text-brand-black font-semibold'
                  : t.red
                    ? 'bg-gray-800 text-red-400/70 hover:bg-gray-700 border border-gray-700'
                    : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
              }`}>
              {t.label}
            </button>
          ))}
          {logType !== 'fail2ban' && (
            <div className="ml-auto flex gap-1.5">
              {['ALL', 'ERROR', 'WARNING', 'INFO', 'DEBUG'].map(lvl => (
                <button key={lvl} onClick={() => setFilterLevel(lvl)}
                  className={`px-2.5 py-1 rounded text-xs transition-colors ${
                    filterLevel === lvl
                      ? lvl === 'ERROR'   ? 'bg-red-500/30 text-red-300 font-bold'
                      : lvl === 'WARNING' ? 'bg-yellow-500/30 text-yellow-300 font-bold'
                      : lvl === 'DEBUG'   ? 'bg-purple-500/30 text-purple-300 font-bold'
                      : 'bg-brand-green/20 text-brand-green font-bold'
                      : 'bg-gray-800 text-gray-500 hover:bg-gray-700'
                  }`}>
                  {lvl}
                </button>
              ))}
            </div>
          )}
          {logType === 'fail2ban' && (
            <button onClick={fetchFail2ban} disabled={fail2banLoading}
              className="ml-auto px-3 py-1.5 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 text-sm flex items-center gap-1.5 disabled:opacity-50">
              <RefreshCw className={`w-3.5 h-3.5 ${fail2banLoading ? 'animate-spin' : ''}`} />
              Actualiser
            </button>
          )}
        </div>
      </div>

      {/* Error */}
      {error && logType !== 'fail2ban' && (
        <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-3 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
          <div>
            <div className="text-red-400 font-medium text-sm">Erreur</div>
            <div className="text-xs text-gray-400">{error}</div>
          </div>
        </div>
      )}

      {/* Fail2ban view */}
      {logType === 'fail2ban' && (
        <div className="bg-brand-black rounded-lg border border-red-500/20 p-4">
          <Fail2banView data={fail2banData} loading={fail2banLoading} onRefresh={fetchFail2ban} />
        </div>
      )}

      {/* Terminal — hidden in fail2ban mode */}
      {logType !== 'fail2ban' && (
        <div className={`bg-black rounded-lg border border-brand-green/30 overflow-hidden ${fullHeight ? 'flex-1 flex flex-col' : ''}`}>
          <div className="bg-brand-black-lighter px-3 py-2 border-b border-brand-green/20 flex items-center gap-2 flex-shrink-0">
            <Terminal className="w-3.5 h-3.5 text-brand-green" />
            <span className="text-xs text-brand-green font-mono">sylink-logs — {logType} ({filteredLogs.length} lignes)</span>
            {loading && <RefreshCw className="w-3 h-3 text-brand-green animate-spin ml-auto" />}
            {!paused && !loading && <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse ml-auto" />}
            {paused && <span className="ml-auto text-xs text-yellow-400 bg-yellow-500/20 px-2 py-0.5 rounded">PAUSE</span>}
          </div>

          <div className={`${fullHeight ? 'flex-1 min-h-0' : 'h-96'} overflow-y-auto p-3 font-mono text-xs`}>
            {loading && filteredLogs.length === 0 ? (
              <div className="text-gray-600 text-center py-12">
                <RefreshCw className="w-10 h-10 mx-auto mb-3 opacity-50 animate-spin" />
                <p>Chargement des logs...</p>
              </div>
            ) : filteredLogs.length === 0 ? (
              <div className="text-gray-600 text-center py-12">
                <Terminal className="w-10 h-10 mx-auto mb-3 opacity-30" />
                <p className="mb-1">Aucun log disponible</p>
                <p className="text-xs">Logs accumulate in real time (auto-reload 5s)</p>
                {logType === 'backend' && (
                  <p className="text-xs mt-2 text-gray-700">Backend logs are written to /app/logs/backend.log</p>
                )}
              </div>
            ) : (
              <>
                {filteredLogs.map(log => (
                  <div key={log.id} className={`mb-0.5 px-2 py-0.5 rounded ${getLogBg(log.level)}`}>
                    <span className="text-gray-600">{formatTimestamp(log.timestamp)}</span>
                    <span className={`mx-1.5 ${log.source === 'backend' ? 'text-blue-400' : log.source === 'system' ? 'text-gray-500' : 'text-purple-400'}`}>
                      [{log.source}]
                    </span>
                    {log.module && <span className="text-gray-600 mr-1.5">[{log.module}]</span>}
                    <span className={`${getLogColor(log.level)} font-semibold`}>[{log.level}]</span>
                    <span className="text-gray-300 ml-1.5">{formatMessage(log)}</span>
                  </div>
                ))}
                <div ref={logsEndRef} />
              </>
            )}
          </div>
        </div>
      )}

      {/* Stats — adapted by tab */}
      {logType !== 'fail2ban' ? (
        <div className="grid grid-cols-5 gap-3">
          <div className="bg-brand-black rounded-lg p-3 border border-gray-800 text-center">
            <div className="text-xl font-bold text-brand-green">{filteredLogs.length}</div>
            <div className="text-xs text-gray-400">Lignes</div>
          </div>
          <div className="bg-brand-black rounded-lg p-3 border border-gray-800 text-center">
            <div className="text-xl font-bold text-red-400">{stats.errors}</div>
            <div className="text-xs text-gray-400">Erreurs</div>
          </div>
          <div className="bg-brand-black rounded-lg p-3 border border-gray-800 text-center">
            <div className="text-xl font-bold text-yellow-400">{stats.warnings}</div>
            <div className="text-xs text-gray-400">Warnings</div>
          </div>
          <div className="bg-brand-black rounded-lg p-3 border border-gray-800 text-center">
            <div className={`text-xl font-bold ${
              disk?.level === 'CRITICAL' ? 'text-red-400' :
              disk?.level === 'WARNING'  ? 'text-yellow-400' : 'text-green-400'
            }`}>
              {disk ? `${disk.percent}%` : '--'}
            </div>
            <div className="text-xs text-gray-400">Disque</div>
          </div>
          <div className="bg-brand-black rounded-lg p-3 border border-gray-800 text-center">
            <div className={`text-xl font-bold ${paused ? 'text-yellow-400' : 'text-green-400'}`}>
              {paused ? 'Pause' : 'Live'}
            </div>
            <div className="text-xs text-gray-400">Status</div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-3 text-center text-xs text-gray-500">
          <div className="bg-brand-black rounded-lg p-2 border border-gray-800">
            fail2ban.log mounted read-only · refresh every 30s
          </div>
          <div className="bg-brand-black rounded-lg p-2 border border-gray-800">
            {disk ? `Disque ${disk.percent}%` : 'Disque --'}
          </div>
          <div className="bg-brand-black rounded-lg p-2 border border-gray-800">
            Jails: {(fail2banData?.stats?.jails ?? []).join(', ') || '--'}
          </div>
        </div>
      )}
    </div>
  );
};

export default LogsViewer;
